import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { auth, players as playersAPI, getCurrentOrganization, setCurrentOrganization, organizations, apiRequest } from '../lib/api'
import SetupWizard from '../components/SetupWizard'
import { buildSubdomainUrl, getOrganizationSlugFromSubdomain } from '../lib/utils'
import { parseLocalDate, formatTime } from '../utils/dateHelpers'
import { getMapUrls } from '../utils/mapHelpers'
import StandingsTable from '../components/tables/StandingsTable'
import PlayerStatsTable from '../components/tables/PlayerStatsTable'
import TeamRosterTable from '../components/tables/TeamRosterTable'
import DelayedSpinner from '../components/DelayedSpinner'
import AnnouncementCard from '../components/cards/AnnouncementCard'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Cache helper functions at module level
const getCachedHomeData = () => {
  try {
    const cached = sessionStorage.getItem('iceops_home_data')
    return cached ? JSON.parse(cached) : null
  } catch {
    return null
  }
}

const setCachedHomeData = (data) => {
  try {
    sessionStorage.setItem('iceops_home_data', JSON.stringify(data))
  } catch (error) {
    console.error('Failed to cache home data:', error)
  }
}

export default function Home() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get cached data on initialization
  const cachedData = getCachedHomeData()

  const [hasLeagues, setHasLeagues] = useState(cachedData?.hasLeagues || false)
  const [leagueData, setLeagueData] = useState(cachedData?.leagueData || []) // Array of {league, standings, upcomingGames}
  const [loading, setLoading] = useState(!cachedData) // Skip loading if we have cached data
  const [selectedLeagueIndex, setSelectedLeagueIndex] = useState(0) // For tabbed view with multiple leagues
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authChecked, setAuthChecked] = useState(false) // Track if initial auth check is complete
  const [user, setUser] = useState(null)
  const [userLeagueIds, setUserLeagueIds] = useState(cachedData?.userLeagueIds || []) // League IDs user has access to
  const [collapsedLeagues, setCollapsedLeagues] = useState(() => {
    const saved = localStorage.getItem('collapsedLeagues')
    return saved ? JSON.parse(saved) : {}
  })
  const [starredLeagues, setStarredLeagues] = useState(() => {
    const saved = localStorage.getItem('starredLeagues')
    return saved ? JSON.parse(saved) : {}
  })
  const [expandedSchedules, setExpandedSchedules] = useState({})
  const [notifySubModal, setNotifySubModal] = useState({ isOpen: false, announcement: null })
  const [subNotificationMessage, setSubNotificationMessage] = useState('')
  const [subNotificationStatus, setSubNotificationStatus] = useState(null) // null, 'success', 'error'
  const [viewMode, setViewMode] = useState('admin') // 'admin' or 'player'
  const [adminIsPlayer, setAdminIsPlayer] = useState(false) // Check if admin also has player profiles
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [hasOrgs, setHasOrgs] = useState(true)
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, orgId: null, orgName: '', deleting: false, error: '' })
  const [allOrganizations, setAllOrganizations] = useState([])
  const [organizationsLoading, setOrganizationsLoading] = useState(true)
  const [activeHomeTab, setActiveHomeTab] = useState('features') // 'features', 'faq', 'support'

  // Handle organization detection from subdomain
  useEffect(() => {
    const subdomain = getOrganizationSlugFromSubdomain()

    // Special handling for "admin" subdomain (admin view of all organizations)
    if (subdomain === 'admin') {
      // Clear any specific org selection for admin view
      setCurrentOrganization(null)
      return
    }

    // Always check if the currently stored org still exists
    const validateCurrentOrg = async () => {
      const currentOrg = getCurrentOrganization()
      if (currentOrg) {
        try {
          const orgs = await organizations.getAll()
          const stillExists = orgs.find(org => org.id === currentOrg.id)
          if (!stillExists) {
            // Organization was deleted, clear localStorage
            setCurrentOrganization(null)
            window.location.reload() // Reload to fetch fresh data
          }
        } catch (error) {
          console.error('Error validating current organization:', error)
        }
      }
    }

    validateCurrentOrg()

    if (!subdomain) return

    // Fetch all organizations and find the one matching the subdomain slug
    const setOrganizationFromSubdomain = async () => {
      try {
        const orgs = await organizations.getAll()
        const matchingOrg = orgs.find(org => org.slug === subdomain)
        if (matchingOrg) {
          setCurrentOrganization(matchingOrg)
          // Trigger organization change event
          window.dispatchEvent(new CustomEvent('organizationChanged', { detail: matchingOrg }))
        } else {
          // Subdomain org doesn't exist, clear localStorage
          setCurrentOrganization(null)
        }
      } catch (error) {
        console.error('Error setting organization from subdomain:', error)
      }
    }

    setOrganizationFromSubdomain()
  }, [])

  useEffect(() => {
    const checkAuth = async () => {
      // Check if we just logged out (prevent auto-redirect loop)
      const justLoggedOut = searchParams.get('logout') === 'true'

      const isAuth = await auth.isAuthenticated()
      setIsAuthenticated(isAuth)
      setAuthChecked(true) // Mark auth check as complete

      // Get user information if authenticated
      if (isAuth && !justLoggedOut) {
        const currentUser = await auth.getUser()
        setUser(currentUser)
        fetchUserLeagues(currentUser)

        // Check subdomain
        const subdomain = getOrganizationSlugFromSubdomain()

        // Auto-redirect authenticated users on main domain to their proper subdomain
        if (!subdomain) {
          // We're on main domain (iceops.app)
          if (currentUser.role === 'admin') {
            // Admins always go to admin page
            navigate('/admin')
            return
          }
          // Note: Players and league managers redirected via login flow to org subdomain
          // If they ended up here, they might not have an org assigned yet
        }

        // Check if admin has organizations (for first-time setup)
        if (currentUser.role === 'admin') {
          checkAdminOrganizations()
        }
      }

      fetchPublicData()
    }

    checkAuth()

    // Listen for organization changes
    const handleOrgChange = () => {
      fetchPublicData()
    }
    window.addEventListener('organizationChanged', handleOrgChange)

    return () => {
      window.removeEventListener('organizationChanged', handleOrgChange)
    }
  }, [searchParams])

  const checkAdminOrganizations = async () => {
    setOrganizationsLoading(true)
    try {
      const allOrgs = await organizations.getAll()
      const hasAnyOrgs = allOrgs && allOrgs.length > 0
      setHasOrgs(hasAnyOrgs)
      setAllOrganizations(allOrgs || [])

      // Admins manage orgs from the admin dashboard, they never see the setup wizard
      // They can create orgs using the "Create Organization" button in the UI
    } catch (error) {
      console.error('Error checking organizations:', error)
      setHasOrgs(false)
      setAllOrganizations([])
    } finally {
      setOrganizationsLoading(false)
    }
  }

  const handleDeleteOrganization = async (orgId) => {
    setDeleteConfirm(prev => ({ ...prev, deleting: true, error: '' }))

    try {
      await organizations.delete(orgId)
      // Success - close modal and reload
      setDeleteConfirm({ isOpen: false, orgId: null, orgName: '', deleting: false, error: '' })
      window.location.reload()
    } catch (error) {
      console.error('Error deleting organization:', error)
      setDeleteConfirm(prev => ({
        ...prev,
        deleting: false,
        error: `Failed to delete organization: ${error.message}`
      }))
    }
  }

  const checkIfAdminIsPlayer = async (currentUser) => {
    if (!currentUser || currentUser.role !== 'admin') return

    try {
      const allPlayers = await apiRequest('/api/players')
      const userPlayers = allPlayers.filter(p => p.user_id === currentUser.id)
      setAdminIsPlayer(userPlayers.length > 0)
    } catch (error) {
      console.error('Error checking if admin is player:', error)
    }
  }

  const fetchUserLeagues = async (currentUser) => {
    if (!currentUser) return

    try {
      // Admin sees all leagues (no filtering needed)
      if (currentUser.role === 'admin') {
        setUserLeagueIds([]) // Empty array means "all leagues" for admin
        // Check if admin is also a player
        await checkIfAdminIsPlayer(currentUser)
        return
      }

      // For league managers, fetch leagues they manage
      if (currentUser.role === 'league_manager') {
        try {
          const managerData = await apiRequest(`/api/league-managers/user/${currentUser.id}`)
          const leagueIds = managerData.map(lm => lm.league_id)
          setUserLeagueIds(leagueIds)
          return
        } catch (error) {
          console.error('Error fetching league manager data:', error)
        }
      }

      // For players, fetch teams they're on to determine which leagues they can see
      const allPlayers = await apiRequest('/api/players')
      const userPlayers = allPlayers.filter(p => p.user_id === currentUser.id)

      if (userPlayers.length > 0) {
        // Get teams for these players
        const allTeams = await apiRequest('/api/teams')
        const userTeams = allTeams.filter(t => userPlayers.some(p => p.team_id === t.id))
        const leagueIds = [...new Set(userTeams.map(t => t.league_id))]
        setUserLeagueIds(leagueIds)
      }
    } catch (error) {
      console.error('Error fetching user leagues:', error)
    }
  }

  const fetchPublicData = async () => {
    try {
      // Get current organization for filtering
      const currentOrg = getCurrentOrganization()
      const orgParam = currentOrg ? `?organization_id=${currentOrg.id}` : ''

      const [leaguesData, teamsData, gamesData, playersData] = await Promise.all([
        apiRequest(`/api/leagues${orgParam}`).catch(() => []),
        apiRequest('/api/teams').catch(() => []),
        apiRequest('/api/games').catch(() => []),
        apiRequest('/api/players').catch(() => []),
      ])

      // Fetch announcements and playoff brackets for all leagues IN PARALLEL
      const [announcementsResults, bracketsResults] = await Promise.all([
        // Fetch announcements for each league
        Promise.all(leaguesData.map(league =>
          apiRequest(`/api/announcements/league/${league.id}`)
            .then(data => ({ leagueId: league.id, data }))
            .catch(() => ({ leagueId: league.id, data: [] }))
        )),
        // Fetch playoff brackets for each league
        Promise.all(leaguesData.map(async (league) => {
          try {
            const activeSeason = await apiRequest(`/api/seasons/league/${league.id}/active`)
            if (activeSeason) {
              const bracket = await apiRequest(`/api/playoffs/league/${league.id}/season/${activeSeason.id}/active`)
              if (bracket) {
                const bracketData = await apiRequest(`/api/playoffs/${bracket.id}`)
                return { leagueId: league.id, data: bracketData }
              }
            }
            return { leagueId: league.id, data: null }
          } catch (error) {
            return { leagueId: league.id, data: null }
          }
        }))
      ])

      // Convert results to objects
      const leagueAnnouncements = {}
      announcementsResults.forEach(result => {
        leagueAnnouncements[result.leagueId] = result.data
      })

      const leagueBrackets = {}
      bracketsResults.forEach(result => {
        leagueBrackets[result.leagueId] = result.data
      })

      if (leaguesData.length > 0) {
        setHasLeagues(true)

        // Calculate standings and upcoming games for each league
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // Get start and end of current week (Sunday to Saturday)
        const currentDay = today.getDay() // 0 (Sunday) to 6 (Saturday)
        const startOfWeek = new Date(today)
        startOfWeek.setDate(today.getDate() - currentDay)
        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 6)
        endOfWeek.setHours(23, 59, 59, 999)

        const allLeagueData = await Promise.all(leaguesData.map(async (league) => {
          const leagueTeams = teamsData.filter(t => t.league_id === league.id)
          const completedGames = gamesData.filter(g =>
            g.home_score != null && g.away_score != null &&
            leagueTeams.some(t => t.id === g.home_team_id || t.id === g.away_team_id)
          )

          // Fetch active season
          let activeSeason = null
          try {
            activeSeason = await apiRequest(`/api/seasons/league/${league.id}/active`)
          } catch (error) {
            // No active season
          }

          // Get point values from active season or use defaults
          const pointsWin = activeSeason?.points_win ?? 2
          const pointsLoss = activeSeason?.points_loss ?? 0
          const pointsTie = activeSeason?.points_tie ?? 1

          // Calculate standings
          const teamStats = {}
          leagueTeams.forEach(team => {
            teamStats[team.id] = {
              team,
              wins: 0,
              losses: 0,
              ties: 0,
              gf: 0,
              ga: 0,
              points: 0,
            }
          })

          completedGames.forEach((game) => {
            const homeTeam = teamStats[game.home_team_id]
            const awayTeam = teamStats[game.away_team_id]

            if (!homeTeam || !awayTeam) return

            homeTeam.gf += game.home_score
            homeTeam.ga += game.away_score
            awayTeam.gf += game.away_score
            awayTeam.ga += game.home_score

            if (game.home_score > game.away_score) {
              homeTeam.wins++
              homeTeam.points += pointsWin
              awayTeam.losses++
              awayTeam.points += pointsLoss
            } else if (game.away_score > game.home_score) {
              awayTeam.wins++
              awayTeam.points += pointsWin
              homeTeam.losses++
              homeTeam.points += pointsLoss
            } else {
              homeTeam.ties++
              awayTeam.ties++
              homeTeam.points += pointsTie
              awayTeam.points += pointsTie
            }
          })

          const standings = Object.values(teamStats).sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points
            return b.gf - b.ga - (a.gf - a.ga)
          })

          // Get upcoming games for this league (current week only) - both regular season and playoff games
          const regularSeasonGames = gamesData.filter(g => {
            const gameDate = new Date(g.game_date)
            const isCurrentWeek = gameDate >= startOfWeek && gameDate <= endOfWeek && !g.home_score
            const isLeagueGame = leagueTeams.some(t => t.id === g.home_team_id || t.id === g.away_team_id)
            return isCurrentWeek && isLeagueGame
          })

          // Get upcoming playoff games
          const playoffGames = []
          if (leagueBrackets[league.id]?.matches) {
            leagueBrackets[league.id].matches.forEach(match => {
              if (match.game_date && !match.winner_id && match.team1_id && match.team2_id) {
                const gameDate = new Date(match.game_date)
                if (gameDate >= startOfWeek && gameDate <= endOfWeek) {
                  playoffGames.push({
                    id: `playoff-${match.id}`,
                    home_team_name: match.team1_name,
                    away_team_name: match.team2_name,
                    game_date: match.game_date,
                    game_time: match.game_time,
                    rink_name: match.rink_name,
                    surface_name: match.surface_name,
                    isPlayoff: true,
                    round: match.round
                  })
                }
              }
            })
          }

          const upcomingGames = [...regularSeasonGames, ...playoffGames]
            .sort((a, b) => new Date(a.game_date) - new Date(b.game_date))

          // Get all games (for full schedule) - include past, present, and future
          const allLeagueGames = gamesData.filter(g => {
            return leagueTeams.some(t => t.id === g.home_team_id || t.id === g.away_team_id)
          }).sort((a, b) => new Date(b.game_date) - new Date(a.game_date)) // Sort newest first

          // Group players by team for this league
          const teamRosters = leagueTeams.reduce((acc, team) => {
            acc[team.id] = playersData
              .filter(p => p.team_id === team.id)
              .sort((a, b) => {
                // Sort by position (captain first), then by jersey number, then by name
                if (a.position === 'captain' && b.position !== 'captain') return -1
                if (b.position === 'captain' && a.position !== 'captain') return 1
                if (a.jersey_number && b.jersey_number) return a.jersey_number - b.jersey_number
                return (a.name || '').localeCompare(b.name || '')
              })
            return acc
          }, {})

          // Fetch player stats for this league
          let playerStats = []
          try {
            playerStats = await playersAPI.getLeagueStats(league.id)
          } catch (error) {
            console.error(`Error fetching player stats for league ${league.id}:`, error)
          }

          return {
            league,
            activeSeason,
            standings,
            upcomingGames,
            allGames: allLeagueGames,
            announcements: leagueAnnouncements[league.id] || [],
            bracket: leagueBrackets[league.id] || null,
            teams: leagueTeams,
            teamRosters,
            playerStats
          }
        }))

        setLeagueData(allLeagueData)

        // Cache the data for instant display on next load
        setCachedHomeData({
          hasLeagues: leaguesData.length > 0,
          leagueData: allLeagueData,
          userLeagueIds
        })
      }
    } catch (error) {
      console.error('Error fetching public data:', error)
    } finally {
      setLoading(false)
    }
  }


  // Check if we're on a subdomain or main domain
  const subdomain = getOrganizationSlugFromSubdomain()
  const isMainDomain = !subdomain
  const isAllOrgsSubdomain = subdomain === 'admin'

  // Handle admin subdomain (admin-only view of all organizations)
  if (isAllOrgsSubdomain) {
    // Show loading while we check authentication
    if (loading) {
      return <DelayedSpinner delay={200} />
    }

    // Require admin authentication for admin subdomain
    if (!isAuthenticated || !user || user.role !== 'admin') {
      return (
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="card text-center">
            <h2 className="section-header text-slate-900 mb-4">Admin Access Required</h2>
            <p className="text-slate-600 mb-6">
              This page is only accessible to system administrators.
            </p>
            <div className="space-y-3">
              <Link to="/login" className="btn-primary">
                Sign In
              </Link>
              <button
                onClick={() => {
                  // Clear everything and force re-login
                  auth.signOut()
                  window.location.href = '/login'
                }}
                className="btn-secondary text-sm"
              >
                Clear Session & Re-login
              </button>
            </div>
          </div>
        </div>
      )
    }
    // Admin is authenticated - show all leagues (continue to league display below)
  }

  // For subdomains, show loading spinner while data is loading
  // This prevents showing marketing content on org subdomains while waiting for league data
  if (loading && !isMainDomain) {
    return <DelayedSpinner delay={200} />
  }

  // On main domain, show loading spinner until auth check completes
  // This prevents flash of marketing content before redirect to dashboard for authenticated users
  if (isMainDomain && !authChecked) {
    return <DelayedSpinner delay={200} />
  }

  // Redirect authenticated users on main domain to dashboard
  // They shouldn't see marketing content when logged in
  if (isMainDomain && isAuthenticated && authChecked) {
    navigate('/dashboard', { replace: true })
    return <DelayedSpinner delay={0} />
  }

  // Show marketing landing page on main domain for unauthenticated users
  // Or on subdomains with no leagues (unless it's the admin subdomain)
  if (isMainDomain && !isAllOrgsSubdomain) {
    return (
      <div className="relative">
        {/* Hero Section - Full viewport width with subtle pattern */}
        <div className="relative left-1/2 right-1/2 -mx-[50vw] w-screen bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 text-white overflow-hidden">
          {/* Subtle hockey rink pattern overlay - hidden on mobile */}
          <div className="absolute inset-0 hidden md:flex items-center justify-center opacity-[0.08]">
            <svg className="w-auto h-[100%] max-w-[100vw]" viewBox="0 0 400 170" preserveAspectRatio="xMidYMid meet">
              {/* Rink outline with rounded corners */}
              <rect x="10" y="10" width="380" height="150" rx="25" ry="25" fill="none" stroke="currentColor" strokeWidth="1.5"/>

              {/* Blue lines */}
              <line x1="133" y1="10" x2="133" y2="160" stroke="#3b82f6" strokeWidth="2"/>
              <line x1="267" y1="10" x2="267" y2="160" stroke="#3b82f6" strokeWidth="2"/>

              {/* Red center line */}
              <line x1="200" y1="10" x2="200" y2="160" stroke="#ef4444" strokeWidth="2"/>

              {/* Center ice circle */}
              <circle cx="200" cy="85" r="20" fill="none" stroke="#ef4444" strokeWidth="1.5"/>
              <circle cx="200" cy="85" r="2" fill="#ef4444"/>

              {/* Face-off circles - left zone */}
              <circle cx="70" cy="50" r="18" fill="none" stroke="#ef4444" strokeWidth="1"/>
              <circle cx="70" cy="50" r="2" fill="#ef4444"/>
              <circle cx="70" cy="120" r="18" fill="none" stroke="#ef4444" strokeWidth="1"/>
              <circle cx="70" cy="120" r="2" fill="#ef4444"/>

              {/* Face-off circles - right zone */}
              <circle cx="330" cy="50" r="18" fill="none" stroke="#ef4444" strokeWidth="1"/>
              <circle cx="330" cy="50" r="2" fill="#ef4444"/>
              <circle cx="330" cy="120" r="18" fill="none" stroke="#ef4444" strokeWidth="1"/>
              <circle cx="330" cy="120" r="2" fill="#ef4444"/>

              {/* Neutral zone face-off dots */}
              <circle cx="155" cy="50" r="2" fill="#ef4444"/>
              <circle cx="155" cy="120" r="2" fill="#ef4444"/>
              <circle cx="245" cy="50" r="2" fill="#ef4444"/>
              <circle cx="245" cy="120" r="2" fill="#ef4444"/>

              {/* Goal lines - full width red lines at each end */}
              <line x1="30" y1="10" x2="30" y2="160" stroke="#ef4444" strokeWidth="2"/>
              <line x1="370" y1="10" x2="370" y2="160" stroke="#ef4444" strokeWidth="2"/>

              {/* Goal crease - left (semicircle curving towards center) */}
              <path d="M 30 65 A 20 20 0 0 1 30 105" fill="none" stroke="#3b82f6" strokeWidth="1.5"/>

              {/* Goal crease - right (semicircle curving towards center) */}
              <path d="M 370 65 A 20 20 0 0 0 370 105" fill="none" stroke="#3b82f6" strokeWidth="1.5"/>
            </svg>
          </div>

          <div className="relative max-w-5xl mx-auto px-4 py-12 md:py-20 text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 tracking-tight">
              <span className="text-white">Run Your League</span>
              <br />
              <span className="text-ice-400">Like a Pro</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Everything you need in one powerful platform. Track teams, schedule games, manage players, automate communications, and collect payments seamlessly.
            </p>
            {!isAuthenticated && (
              <>
                <div className="flex justify-center mb-8">
                  <Link to="/login?signup=true&type=org_owner" className="bg-ice-500 hover:bg-ice-400 text-white text-lg px-10 py-4 rounded-lg font-semibold transition-all shadow-lg shadow-ice-500/25 hover:shadow-ice-400/30">
                    Start Your Free Trial
                  </Link>
                </div>
                <div className="flex items-center justify-center gap-4 mb-6 text-sm">
                  <span className="flex items-center gap-2 text-slate-300">
                    <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    14-day free trial
                  </span>
                  <span className="flex items-center gap-2 text-slate-300">
                    <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    No credit card required
                  </span>
                  <span className="flex items-center gap-2 text-slate-300">
                    <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    Starting at $25/mo
                  </span>
                </div>
                <p className="text-sm text-slate-400 mb-3">
                  <Link to="/pricing" className="text-ice-400 hover:text-ice-300 font-medium underline underline-offset-2">View Pricing</Link>
                </p>
                <div className="text-sm text-slate-500">
                  <p className="mb-1">Looking to join a league?</p>
                  <p className="flex items-center justify-center gap-2">
                    <Link to="/login?signup=true&type=player" className="text-ice-400 hover:text-ice-300 font-medium">Player Sign Up</Link>
                    <span>•</span>
                    <Link to="/login?signup=true&type=referee" className="text-ice-400 hover:text-ice-300 font-medium">Referee Sign Up</Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content Section with integrated tabs */}
        <div className="max-w-5xl mx-auto px-4 pt-8 pb-8">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border border-slate-200">
              <button
                onClick={() => setActiveHomeTab('features')}
                className={`py-2.5 px-6 font-medium text-sm transition-all rounded-md ${
                  activeHomeTab === 'features'
                    ? 'bg-ice-500 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                Features
              </button>
              <button
                onClick={() => setActiveHomeTab('faq')}
                className={`py-2.5 px-6 font-medium text-sm transition-all rounded-md ${
                  activeHomeTab === 'faq'
                    ? 'bg-ice-500 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                FAQ
              </button>
              <button
                onClick={() => setActiveHomeTab('support')}
                className={`py-2.5 px-6 font-medium text-sm transition-all rounded-md ${
                  activeHomeTab === 'support'
                    ? 'bg-ice-500 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                Support & Contact
              </button>
            </div>
          </div>
        {/* Features Tab */}
        {activeHomeTab === 'features' && (
          <div className="card mb-12">
            <h2 className="section-header text-slate-900 mb-6 text-center">Everything You Need to Manage Your League</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Your Own Organization</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Create your own branded organization with a custom subdomain. Run multiple leagues under one subscription.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">League Management</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Unlimited leagues and seasons. Customize point systems, track payment periods, and manage team rosters independently.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Live Stats & Standings</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Automatic standings and player stats tracking. Monitor goals, assists, points, penalty minutes, and +/- in real-time.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Payment Tracking</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Track player dues with support for Venmo, PayPal, Zelle, Cash, and Check. Visual progress shows payment status at a glance.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Attendance Tracking</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Players mark "I'm In" or "I'm Out" for games. Captains see real-time attendance with one-click email confirmations.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Automated Scheduling</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Generate full round-robin schedules or elimination brackets automatically. Set game times and let IceOps do the rest.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Team Discovery</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Public team directory lets players find and join teams. Captains review and approve join requests with one click.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Team & Player Management</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Unlimited teams and players. Assign jersey numbers, positions, link to user accounts, and track player history.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Schedule Management</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Schedule games across rinks with integrated maps. Export to Google, Apple, or Outlook calendars with one click.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Broadcast Messaging</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Send mass emails or SMS to your entire organization, league, or team. Automated weekly digests and captain reminders included.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Playoff Brackets</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Create tournaments with automatic bracket generation. Support for 4, 8, or 16 team playoffs with match tracking.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Custom Domains</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Use your own domain name. Each organization gets a unique subdomain for professional branding.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Mobile Optimized</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Fully responsive design works perfectly on phones, tablets, and desktops. Manage your league on the go.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Enterprise Security</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Role-based access control, automated backups, audit logging, rate limiting, and bcrypt password encryption.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Referee Marketplace</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Find certified referees or create your referee profile. Search by location, availability, and ratings. Book officials and leave reviews after games.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-900">Scramble Leagues</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Automatically reshuffle teams weekly, per-season, or on-demand. Balance teams by skill ratings using smart algorithms. View scramble history and manage configurations.
              </p>
            </div>
          </div>
          </div>
        )}

        {/* FAQ Tab */}
        {activeHomeTab === 'faq' && (
          <div className="card mb-12">
            <h2 className="section-header text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-6">
            <details className="group">
              <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <span className="font-semibold text-slate-900">How does the 14-day free trial work?</span>
                <svg className="w-5 h-5 text-slate-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 text-slate-600">
                Start using IceOps immediately with full access to all features. No credit card required to sign up. After 14 days, choose a plan that fits your needs or your organization will be paused until you subscribe.
              </div>
            </details>

            <details className="group">
              <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <span className="font-semibold text-slate-900">Can players use IceOps for free?</span>
                <svg className="w-5 h-5 text-slate-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 text-slate-600">
                Yes! Player accounts are always free. Players can view schedules, check standings, mark attendance, see their stats, and receive team communications at no cost.
              </div>
            </details>

            <details className="group">
              <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <span className="font-semibold text-slate-900">How many leagues can I run under one organization?</span>
                <svg className="w-5 h-5 text-slate-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 text-slate-600">
                All plans include unlimited leagues, teams, and players. Run as many leagues as you need under one organization subscription.
              </div>
            </details>

            <details className="group">
              <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <span className="font-semibold text-slate-900">How does the referee marketplace work?</span>
                <svg className="w-5 h-5 text-slate-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 text-slate-600">
                Referees create profiles with their certifications, rates, and availability. League managers can search for referees by location, date, and ratings, then book them directly for games. Players can leave reviews after games.
              </div>
            </details>

            <details className="group">
              <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <span className="font-semibold text-slate-900">Can I cancel anytime?</span>
                <svg className="w-5 h-5 text-slate-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 text-slate-600">
                Yes, you can cancel your subscription at any time. Your data is preserved and you can reactivate whenever you're ready. No long-term contracts or cancellation fees.
              </div>
            </details>
          </div>
          </div>
        )}

        {/* Support & Contact Tab */}
        {activeHomeTab === 'support' && (
          <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-8 h-8 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <h2 className="text-2xl font-bold text-slate-900">Support</h2>
            </div>
            <p className="text-slate-600 mb-6">
              Get help with setup, features, and troubleshooting. Our documentation and support team are here to help.
            </p>
            <div className="space-y-3">
              <a
                href="https://docs.iceops.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <div>
                  <div className="font-medium text-slate-900">Documentation</div>
                  <div className="text-sm text-slate-600">Guides, tutorials, and FAQs</div>
                </div>
              </a>
              <a
                href="mailto:support@iceops.app"
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <div className="font-medium text-slate-900">Email Support</div>
                  <div className="text-sm text-slate-600">support@iceops.app</div>
                </div>
              </a>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-8 h-8 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h2 className="text-2xl font-bold text-slate-900">Contact Us</h2>
            </div>
            <p className="text-slate-600 mb-6">
              Have questions about pricing, features, or custom solutions? We'd love to hear from you.
            </p>
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="font-medium text-slate-900 mb-1">General Inquiries</div>
                <a href="mailto:hello@iceops.app" className="text-ice-600 hover:text-ice-700">
                  hello@iceops.app
                </a>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="font-medium text-slate-900 mb-1">Sales & Partnerships</div>
                <a href="mailto:sales@iceops.app" className="text-ice-600 hover:text-ice-700">
                  sales@iceops.app
                </a>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="font-medium text-slate-900 mb-1">Feature Requests</div>
                <a href="https://github.com/drewtwitchell/iceops/issues/new?labels=enhancement&title=[Feature Request]:%20&body=%23%23%20Feature%20Description%0A%0ADescribe%20the%20feature%20you%27d%20like%20to%20see.%0A%0A%23%23%20Use%20Case%0A%0AHow%20would%20this%20help%20you%20manage%20your%20league%3F" target="_blank" rel="noopener noreferrer" className="text-ice-600 hover:text-ice-700">
                  Submit on GitHub →
                </a>
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Welcome Back - For Authenticated Users */}
        {isAuthenticated && (
          <div className="card text-center">
            <h2 className="section-header text-slate-900 mb-3">Welcome Back!</h2>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              {user?.role === 'admin'
                ? 'Access all organizations and leagues across the platform.'
                : 'View your league standings, schedule, and manage your team.'}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              {user?.role === 'admin' ? (
                <>
                  <Link to="/admin" className="btn-primary">
                    Manage All Organizations
                  </Link>
                  <Link to="/dashboard" className="btn-secondary">
                    Go to Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/dashboard" className="btn-primary">
                    Go to Dashboard
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    )
  }

  // Handle subdomains with no leagues - show a helpful message instead of empty page
  if (!isMainDomain && !hasLeagues && !isAllOrgsSubdomain && !loading) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No Leagues Yet</h2>
          <p className="text-slate-600 mb-6">
            This organization doesn't have any active leagues at the moment.
          </p>
          {!isAuthenticated && (
            <Link to="/login" className="btn-primary">
              Sign In
            </Link>
          )}
        </div>
      </div>
    )
  }

  const toggleLeagueCollapse = (leagueId) => {
    const newCollapsed = { ...collapsedLeagues, [leagueId]: !collapsedLeagues[leagueId] }
    setCollapsedLeagues(newCollapsed)
    localStorage.setItem('collapsedLeagues', JSON.stringify(newCollapsed))
  }

  const toggleLeagueStar = (leagueId) => {
    const newStarred = { ...starredLeagues, [leagueId]: !starredLeagues[leagueId] }
    setStarredLeagues(newStarred)
    localStorage.setItem('starredLeagues', JSON.stringify(newStarred))
  }

  const toggleScheduleExpanded = (leagueId) => {
    setExpandedSchedules(prev => ({ ...prev, [leagueId]: !prev[leagueId] }))
  }

  const handleNotifySubClick = (announcement) => {
    if (!isAuthenticated) {
      window.location.href = `/login?returnUrl=${encodeURIComponent('/dashboard')}`
      return
    }
    // Redirect to dashboard where sub confirmation happens
    window.location.href = '/dashboard'
  }

  const handleSubmitSubNotification = async (e) => {
    e.preventDefault()

    try {
      await apiRequest(`/api/announcements/${notifySubModal.announcement.id}/notify-available`, {
        method: 'POST',
        body: JSON.stringify({ message: subNotificationMessage })
      })

      setSubNotificationStatus('success')
      setTimeout(() => {
        setNotifySubModal({ isOpen: false, announcement: null })
        setSubNotificationMessage('')
        setSubNotificationStatus(null)
      }, 2000)
    } catch (error) {
      console.error('Error sending notification:', error)
      setSubNotificationStatus('error')
    }
  }

  // Start with all leagues (already filtered by organization at API level)
  let displayLeagues = leagueData

  // Apply role-based filtering for authenticated users
  if (isAuthenticated && user) {
    if (user.role === 'admin') {
      // Admin sees all leagues - no filtering needed
    } else if (user.role === 'league_manager' || user.role === 'player') {
      // League managers and players only see leagues they have access to
      if (userLeagueIds.length > 0) {
        displayLeagues = displayLeagues.filter(({ league }) =>
          userLeagueIds.includes(league.id)
        )
      } else {
        // If user has no league assignments, show no leagues
        displayLeagues = []
      }
    }
  }

  // Sort leagues: starred leagues first, then by name
  displayLeagues = displayLeagues.sort((a, b) => {
    const aStarred = starredLeagues[a.league.id] || false
    const bStarred = starredLeagues[b.league.id] || false
    if (aStarred && !bStarred) return -1
    if (!aStarred && bStarred) return 1
    return a.league.name.localeCompare(b.league.name)
  })

  const isSingleLeague = displayLeagues.length === 1
  const isMultipleLeagues = displayLeagues.length > 1

  // Public league view
  // Get current organization info for page title
  const currentOrg = getCurrentOrganization()

  // Admin Dashboard View (for admin subdomain when in admin mode)
  if (isAllOrgsSubdomain && viewMode === 'admin') {
    // Show loading while organizations are being fetched
    if (organizationsLoading) {
      return <div className="text-center py-12">Loading organizations...</div>
    }

    // Show setup wizard if triggered (either first-time or manually)
    if (showSetupWizard) {
      return (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {hasOrgs ? 'Create New Organization' : 'Welcome to IceOps!'}
            </h1>
            <p className="text-slate-600">
              {hasOrgs ? 'Add another organization to manage' : 'Let\'s get your first organization set up'}
            </p>
          </div>
          <SetupWizard
            onComplete={() => {
              setShowSetupWizard(false)
              setHasOrgs(true)
              // Clear dismissal flag since org was created
              sessionStorage.removeItem('setupWizardDismissed')
              // Refresh data
              window.location.reload()
            }}
            onDismiss={() => {
              setShowSetupWizard(false)
              // Mark wizard as dismissed for this session
              sessionStorage.setItem('setupWizardDismissed', 'true')
            }}
          />
        </div>
      )
    }

    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                Admin Dashboard
              </h1>
              <p className="text-slate-600">
                Manage all organizations, leagues, and users across the platform
              </p>
            </div>
            {adminIsPlayer && (
              <button
                onClick={() => setViewMode('player')}
                className="btn-secondary flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Switch to Player View
              </button>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {allOrganizations.length > 0 && (
            <button
              onClick={() => setShowSetupWizard(true)}
              className="btn-primary flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Organization
            </button>
          )}
          <Link
            to="/users"
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Manage Users
          </Link>
          <Link
            to="/settings"
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Admin Settings
          </Link>
        </div>

        {/* Admin Quick Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {new Set(displayLeagues.map(d => d.league.organization_id)).size}
                </div>
                <p className="text-sm text-slate-600">Organizations</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {displayLeagues.length}
                </div>
                <p className="text-sm text-slate-600">Total Leagues</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {displayLeagues.reduce((sum, d) => sum + (d.teams?.length || 0), 0)}
                </div>
                <p className="text-sm text-slate-600">Total Teams</p>
              </div>
            </div>
          </div>
        </div>

        {/* Organizations & Leagues Management */}
        <div className="space-y-6">
          {allOrganizations.map((org) => {
            const orgLeagues = displayLeagues.filter(({ league }) => league.organization_id === org.id)
            return (
              <div key={org.id} className="card">
                <div className="flex items-center justify-between mb-4 pb-4 border-b">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{org.name}</h2>
                    {org.description && (
                      <p className="text-sm text-slate-600 mt-1">{org.description}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      {orgLeagues.length} {orgLeagues.length === 1 ? 'League' : 'Leagues'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/organizations"
                      className="btn-secondary flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Organization
                    </Link>
                    <button
                      onClick={() => {
                        setDeleteConfirm({ isOpen: true, orgId: org.id, orgName: org.name, deleting: false, error: '' })
                      }}
                      className="btn-secondary text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Organization
                    </button>
                  </div>
                </div>

                {orgLeagues.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">League</th>
                          <th className="text-center py-3 px-4">Teams</th>
                          <th className="text-center py-3 px-4">Status</th>
                          <th className="text-right py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orgLeagues.map(({ league, teams, activeSeason }) => (
                          <tr key={league.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{league.name}</td>
                            <td className="text-center py-3 px-4">{teams?.length || 0}</td>
                            <td className="text-center py-3 px-4">
                              {activeSeason ? (
                                <span className="badge badge-success text-xs">{activeSeason.name}</span>
                              ) : (
                                <span className="badge badge-secondary text-xs">No Active Season</span>
                              )}
                            </td>
                            <td className="text-right py-3 px-4">
                              <Link
                                to={`/leagues/${league.id}`}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Manage →
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No leagues in this organization</p>
                )}
              </div>
            )
          })}

          {allOrganizations.length === 0 && (
            <div className="card text-center py-8">
              <p className="text-slate-500 mb-4">No organizations found</p>
              <button
                onClick={() => setShowSetupWizard(true)}
                className="btn-primary"
              >
                Create Your First Organization
              </button>
            </div>
          )}
        </div>

        {/* Delete Organization Confirmation Modal */}
        {deleteConfirm.isOpen && (
          <div className="modal-overlay">
            <div className="modal-container">
              <h3 className="modal-header">
                Delete Organization
              </h3>
              <div className="modal-body">
                <p className="mb-4">
                  Are you sure you want to delete <strong>{deleteConfirm.orgName}</strong>?
                  This will permanently delete the organization and all its leagues, teams, players, games, and related data.
                </p>
                <div className="alert alert-error">
                  <p className="font-medium">
                    ⚠️ This action cannot be undone!
                  </p>
                </div>

                {deleteConfirm.error && (
                  <div className="alert alert-error mt-4">
                    {deleteConfirm.error}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => setDeleteConfirm({ isOpen: false, orgId: null, orgName: '', deleting: false, error: '' })}
                  className="btn-secondary"
                  disabled={deleteConfirm.deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteOrganization(deleteConfirm.orgId)}
                  className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={deleteConfirm.deleting}
                >
                  {deleteConfirm.deleting ? 'Deleting...' : 'Delete Organization'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8 text-center">
        {/* View toggle for admins who are also players */}
        {isAllOrgsSubdomain && adminIsPlayer && viewMode === 'player' && (
          <div className="mb-4">
            <button
              onClick={() => setViewMode('admin')}
              className="btn-secondary text-sm flex items-center gap-2 mx-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Admin Dashboard
            </button>
          </div>
        )}

        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
          {isAllOrgsSubdomain
            ? 'All Organizations - Player View'
            : currentOrg && isMultipleLeagues
            ? currentOrg.name
            : isSingleLeague && displayLeagues[0]?.league.name
            ? displayLeagues[0].league.name
            : 'League Standings & Schedule'}
        </h1>
        {isAllOrgsSubdomain && (
          <p className="text-slate-600 mb-3">
            Viewing leagues where you are a player
          </p>
        )}
        {currentOrg && isMultipleLeagues && (
          <p className="text-slate-600 mb-3">
            {displayLeagues.length} {displayLeagues.length === 1 ? 'League' : 'Leagues'}
          </p>
        )}
        {isSingleLeague && displayLeagues[0]?.activeSeason && (
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="badge badge-success text-sm">
              {displayLeagues[0].activeSeason.name}
            </span>
            {displayLeagues[0].activeSeason.start_date && displayLeagues[0].activeSeason.end_date && (
              <span className="text-sm text-slate-500">
                {new Date(displayLeagues[0].activeSeason.start_date).toLocaleDateString()} - {new Date(displayLeagues[0].activeSeason.end_date).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
        {isSingleLeague && displayLeagues[0]?.league.description && (
          <p className="text-slate-600 mb-3">
            {displayLeagues[0].league.description}
          </p>
        )}
        {isSingleLeague && displayLeagues[0]?.league.league_info && (
          <div className="max-w-3xl mx-auto mt-4 p-4 bg-slate-50 rounded-lg">
            <div className="text-slate-700 whitespace-pre-wrap text-sm">
              {displayLeagues[0].league.league_info}
            </div>
          </div>
        )}
      </div>

      {/* Tabs Navigation - for multiple leagues */}
      {isMultipleLeagues && (
        <div className="mb-6">
          <div className="border-b border-slate-200">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide" aria-label="League Tabs">
              {displayLeagues.map((leagueData, index) => (
                <button
                  key={leagueData.league.id}
                  onClick={() => setSelectedLeagueIndex(index)}
                  className={`
                    whitespace-nowrap flex flex-col gap-1 py-3 sm:py-4 px-1 border-b-2 font-medium text-sm sm:text-base transition-colors min-h-[44px]
                    ${selectedLeagueIndex === index
                      ? 'border-ice-600 text-ice-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }
                  `}
                >
                  <span>{leagueData.league.name}</span>
                  {leagueData.activeSeason && (
                    <span className="text-xs text-slate-500">
                      {leagueData.activeSeason.name}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {(isMultipleLeagues ? [displayLeagues[selectedLeagueIndex]] : displayLeagues).map(({ league, activeSeason, standings, upcomingGames, allGames, announcements, bracket, teams, teamRosters, playerStats }) => {
        const isCollapsed = collapsedLeagues[league.id] || false
        const isStarred = starredLeagues[league.id] || false
        const isScheduleExpanded = expandedSchedules[league.id] || false

        return (
          <div key={league.id} className={`mb-12 ${isMultipleLeagues ? 'border-2 border-slate-200 rounded-lg p-6 bg-white shadow-sm' : ''}`}>
            {/* League Header - show simplified version for multiple leagues with tabs */}
            {isMultipleLeagues && (
              <div className="mb-6 pb-4 border-b-2 border-slate-200">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1">
                    <h2 className="section-header text-slate-900 mb-2">
                      {league.name}
                    </h2>
                    {league.description && (
                      <p className="text-slate-600 mb-2">{league.description}</p>
                    )}
                    {league.league_info && (
                      <div className="mt-3 p-3 bg-slate-50 rounded text-sm text-slate-700 whitespace-pre-wrap">
                        {league.league_info}
                      </div>
                    )}
                  </div>
                  {activeSeason && (
                    <div className="flex flex-col items-end gap-2">
                      <span className="badge badge-success text-sm px-3 py-1">
                        {activeSeason.name}
                      </span>
                      {activeSeason.start_date && activeSeason.end_date && (
                        <span className="text-xs text-slate-500">
                          {new Date(activeSeason.start_date).toLocaleDateString()} - {new Date(activeSeason.end_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* League content - wrapper for collapsible content */}
            {(!isMultipleLeagues || !isCollapsed) && (
            <div>

          {/* Announcements - topmost element */}
          {announcements && announcements.length > 0 && (
            <div className="mb-6 space-y-2">
              {announcements.map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  showActions={false}
                  onRespond={announcement.announcement_type === 'sub_request' ? () => handleNotifySubClick(announcement) : null}
                />
              ))}
            </div>
          )}

          {/* Playoff Bracket */}
          {bracket && bracket.bracket && bracket.matches && (
            <div className="card mb-8">
              <h3 className="section-header mb-4 flex items-center gap-2 flex-wrap">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {isMultipleLeagues ? `${league.name} - ${bracket.bracket.name}` : bracket.bracket.name}
                  {isMultipleLeagues && activeSeason && (
                    <span className="text-sm font-normal text-slate-500 ml-2">({activeSeason.name})</span>
                  )}
                </span>
              </h3>
              <div className="md:hidden text-center text-xs text-slate-500 mb-2 flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                Swipe to see all rounds
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
              <div className="overflow-x-auto pb-4 -mx-4 px-4">
                <div className="inline-flex gap-4 md:gap-8 min-w-full">
                  {(() => {
                    const matchesByRound = {}
                    bracket.matches.forEach(match => {
                      if (!matchesByRound[match.round]) {
                        matchesByRound[match.round] = []
                      }
                      matchesByRound[match.round].push(match)
                    })
                    const numRounds = Object.keys(matchesByRound).length

                    return Object.keys(matchesByRound).sort((a, b) => parseInt(a) - parseInt(b)).map(round => (
                      <div key={round} className="flex-1 min-w-[250px]">
                        <h4 className="font-semibold text-slate-900 mb-3 text-center">
                          {round === '1' ? 'Round 1' :
                           round === '2' && numRounds === 2 ? 'Finals' :
                           round === '2' && numRounds === 3 ? 'Semifinals' :
                           round === '2' && numRounds === 4 ? 'Quarterfinals' :
                           round === '3' && numRounds === 3 ? 'Finals' :
                           round === '3' && numRounds === 4 ? 'Semifinals' :
                           round === '4' ? 'Finals' :
                           `Round ${round}`}
                        </h4>
                        <div className="space-y-6">
                          {matchesByRound[round].map((match) => (
                            <div key={match.id} className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden shadow-sm">
                              {/* Team 1 */}
                              <div className={`p-2 border-b ${
                                match.winner_id === match.team1_id
                                  ? 'bg-green-50 border-green-200'
                                  : match.winner_id
                                  ? 'bg-slate-50'
                                  : 'bg-white'
                              }`}>
                                {match.team1_id ? (
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-3 h-3 rounded"
                                        style={{ backgroundColor: match.team1_color || '#ccc' }}
                                      />
                                      <span className="text-sm font-medium">{match.team1_name}</span>
                                      {match.winner_id === match.team1_id && (
                                        <span className="text-green-600 text-xs">✓</span>
                                      )}
                                    </div>
                                    {match.team1_score !== null && (
                                      <span className="font-bold">{match.team1_score}</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400 italic text-sm">TBD</span>
                                )}
                              </div>

                              {/* Team 2 */}
                              <div className={`p-2 ${
                                match.winner_id === match.team2_id
                                  ? 'bg-green-50'
                                  : match.winner_id
                                  ? 'bg-slate-50'
                                  : 'bg-white'
                              }`}>
                                {match.team2_id ? (
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-3 h-3 rounded"
                                        style={{ backgroundColor: match.team2_color || '#ccc' }}
                                      />
                                      <span className="text-sm font-medium">{match.team2_name}</span>
                                      {match.winner_id === match.team2_id && (
                                        <span className="text-green-600 text-xs">✓</span>
                                      )}
                                    </div>
                                    {match.team2_score !== null && (
                                      <span className="font-bold">{match.team2_score}</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400 italic text-sm">TBD</span>
                                )}
                              </div>

                              {/* Match Details */}
                              {(match.game_date || match.rink_name) && (
                                <div className="px-2 py-1 bg-slate-50 border-t text-xs text-slate-600">
                                  {match.game_date && (
                                    <div>
                                      {parseLocalDate(match.game_date).toLocaleDateString()}
                                      {match.game_time && ` at ${formatTime(match.game_time)}`}
                                    </div>
                                  )}
                                  {match.rink_name && (
                                    <div className="flex items-center justify-between gap-2 mt-1">
                                      <span>{match.rink_name}</span>
                                      <div className="flex gap-1">
                                        <a
                                          href={getMapUrls(match.rink_name).google}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-1.5 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs transition-colors"
                                          title="Google Maps"
                                        >
                                          G
                                        </a>
                                        <a
                                          href={getMapUrls(match.rink_name).apple}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs transition-colors"
                                          title="Apple Maps"
                                        >
                                          A
                                        </a>
                                        <a
                                          href={getMapUrls(match.rink_name).waze}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-1.5 py-0.5 bg-cyan-100 hover:bg-cyan-200 text-cyan-700 rounded text-xs transition-colors"
                                          title="Waze"
                                        >
                                          W
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Standings */}
            <div className="card">
              <h3 className="section-header mb-4">
                {isMultipleLeagues ? `${league.name} - Standings` : 'Standings'}
                {isMultipleLeagues && activeSeason && (
                  <span className="text-sm font-normal text-slate-500 ml-2">({activeSeason.name})</span>
                )}
              </h3>
              {standings.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No games played yet</p>
              ) : (
                <StandingsTable
                  standings={standings}
                  showExtendedStats={true}
                  limit={8}
                />
              )}
            </div>

            {/* Upcoming Games */}
            <div className="card">
              <h3 className="section-header mb-4">
                {isMultipleLeagues ? `${league.name} - Upcoming Games` : 'Upcoming Games This Week'}
                {isMultipleLeagues && activeSeason && (
                  <span className="text-sm font-normal text-slate-500 ml-2">({activeSeason.name})</span>
                )}
              </h3>

              {upcomingGames.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No upcoming games this week</p>
              ) : (
                <div className="space-y-3">
                  {upcomingGames.map((game) => (
                    <div key={game.id} className={`p-3 rounded ${game.isPlayoff || game.bracket_id ? 'bg-yellow-50 border border-yellow-200' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-sm">
                          {game.home_team_name} vs {game.away_team_name}
                        </span>
                        {(game.isPlayoff || game.bracket_id) && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded font-medium">
                            Playoff
                          </span>
                        )}
                        {game.round && game.round === 'semifinal' && (
                          <span className="text-xs px-2 py-0.5 bg-blue-200 text-blue-800 rounded font-medium">
                            Semifinal
                          </span>
                        )}
                        {game.round && game.round === 'final' && (
                          <span className="text-xs px-2 py-0.5 bg-green-200 text-green-800 rounded font-medium">
                            Championship
                          </span>
                        )}
                        {game.round && game.round === 'consolation' && (
                          <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-800 rounded font-medium">
                            3rd Place
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600">
                        {parseLocalDate(game.game_date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })} at {formatTime(game.game_time)}
                      </div>
                      {(game.rink_name || game.location) && (
                        <div className="text-xs mt-2">
                          {game.rink_name && (
                            <div className="text-slate-600 font-medium mb-0.5">{game.rink_name}</div>
                          )}
                          {game.location && (
                            <div className="text-slate-500 mb-1">{game.location}</div>
                          )}
                          {game.location && (
                            <div className="flex gap-2">
                            <a
                              href={getMapUrls(game.location).google}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:opacity-75 transition-opacity"
                              title="Open in Google Maps"
                            >
                              <img src="/icons/google-maps.png" alt="Google Maps" className="w-6 h-6" />
                            </a>
                            <a
                              href={getMapUrls(game.location).apple}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:opacity-75 transition-opacity"
                              title="Open in Apple Maps"
                            >
                              <img src="/icons/apple-maps.ico" alt="Apple Maps" className="w-6 h-6" />
                            </a>
                            <a
                              href={getMapUrls(game.location).waze}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:opacity-75 transition-opacity"
                              title="Open in Waze"
                            >
                              <img src="/icons/waze.ico" alt="Waze" className="w-6 h-6" />
                            </a>
                          </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Full Schedule Button and Content */}
              <div className="mt-6 pt-4 border-t">
                <button
                  onClick={() => toggleScheduleExpanded(league.id)}
                  className="btn-secondary btn-sm w-full"
                >
                  {isScheduleExpanded ? 'Hide Full Schedule' : 'View Full Schedule'}
                </button>

                {isScheduleExpanded && (
                  <div className="mt-4">
                    {allGames && allGames.length > 0 ? (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {allGames.map((game) => (
                          <div key={game.id} className={`p-3 rounded border ${
                            game.home_score !== null && game.away_score !== null ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'
                          }`}>
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">
                                  {game.home_team_name} vs {game.away_team_name}
                                </div>
                                <div className="text-xs text-slate-600 mt-0.5">
                                  {parseLocalDate(game.game_date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })} • {formatTime(game.game_time)}
                                </div>
                                {(game.rink_name || game.location) && (
                                  <div className="text-xs mt-1">
                                    {game.rink_name && (
                                      <div className="text-slate-600">{game.rink_name}</div>
                                    )}
                                    {game.location && (
                                      <div className="text-slate-500">{game.location}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex-shrink-0 text-right">
                                {game.home_score !== null && game.away_score !== null ? (
                                  <div className="font-bold text-lg">
                                    {game.home_score} - {game.away_score}
                                  </div>
                                ) : (
                                  <div className="text-slate-400 italic text-xs">Not played</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-8">No games scheduled</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Player Stats */}
          {playerStats && playerStats.length > 0 && (
            <div className="card mb-8">
              <h3 className="section-header mb-4">
                {isMultipleLeagues ? `${league.name} - Player Stats` : 'Player Stats'}
                {isMultipleLeagues && activeSeason && (
                  <span className="text-sm font-normal text-slate-500 ml-2">({activeSeason.name})</span>
                )}
              </h3>
              <PlayerStatsTable
                stats={playerStats}
                showTeamInfo={true}
                compact={false}
              />
            </div>
          )}

          {/* Team Rosters */}
          {teams && teams.length > 0 && (
            <div className="card">
              <h3 className="section-header mb-4">
                {isMultipleLeagues ? `${league.name} - Team Rosters` : 'Team Rosters'}
                {isMultipleLeagues && activeSeason && (
                  <span className="text-sm font-normal text-slate-500 ml-2">({activeSeason.name})</span>
                )}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {teams.map((team) => (
                  <div key={team.id} className="border border-slate-200 rounded-lg p-3 sm:p-4 bg-white">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: team.color }}
                      />
                      <h4 className="font-semibold text-slate-900">{team.name}</h4>
                    </div>
                    <TeamRosterTable
                      players={teamRosters[team.id] || []}
                      teamColor={team.color}
                      compact={true}
                      emptyMessage="No players on roster"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calendar Subscription */}
          <div className="card mt-8">
            <h3 className="section-header mb-3">
              {isMultipleLeagues ? `${league.name} - Subscribe to Game Calendar` : 'Subscribe to Game Calendar'}
            </h3>
            <p className="text-slate-600 mb-4">
              Subscribe to automatically sync games to your calendar app (Google Calendar, Apple Calendar, Outlook, etc.). Your calendar will stay up-to-date as new games are added.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">All League Games</label>
                <a
                  href={`${API_URL}/api/calendar/league/${league.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary btn-sm inline-block"
                >
                  Subscribe to All Games
                </a>
              </div>
              {teams && teams.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Individual Team Games</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {teams.map((team) => (
                      <a
                        key={team.id}
                        href={`${API_URL}/api/calendar/team/${team.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary text-xs py-2 flex items-center gap-2"
                      >
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: team.color }}
                        />
                        {team.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="alert alert-info text-xs">
              <p className="font-semibold mb-1">📅 How to subscribe:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Desktop:</strong> Click a link above to open in your calendar app</li>
                <li><strong>iPhone/iPad:</strong> Tap a link and choose "Subscribe" when prompted</li>
              </ul>
              <p className="mt-2 text-slate-600">Calendars refresh automatically every hour with new games and updates.</p>
            </div>
          </div>
          </div>
          )}
        </div>
      )
    })}

      {/* Notify Sub Availability Modal */}
      {notifySubModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3 className="modal-header">
              Notify Team Captain
            </h3>
            <div className="modal-body">
            {notifySubModal.announcement && (
              <div className="mb-4 p-3 bg-slate-50 rounded">
                <p className="font-semibold text-sm">{notifySubModal.announcement.title}</p>
                {notifySubModal.announcement.game_date && (
                  <p className="text-xs text-slate-600 mt-1">
                    {parseLocalDate(notifySubModal.announcement.game_date).toLocaleDateString()} at {formatTime(notifySubModal.announcement.game_time)}
                    {notifySubModal.announcement.rink_name && ` - ${notifySubModal.announcement.rink_name}`}
                  </p>
                )}
              </div>
            )}

            {subNotificationStatus === 'success' ? (
              <div className="alert alert-success mb-4">
                Success! The team captain has been notified of your availability.
              </div>
            ) : subNotificationStatus === 'error' ? (
              <div className="alert alert-error mb-4">
                Error sending notification. Please try again or contact the captain directly.
              </div>
            ) : (
              <form onSubmit={handleSubmitSubNotification}>
                <div className="mb-4">
                  <label className="label">
                    Optional Message
                  </label>
                  <textarea
                    value={subNotificationMessage}
                    onChange={(e) => setSubNotificationMessage(e.target.value)}
                    className="input w-full"
                    rows="3"
                    placeholder="e.g., I can play forward or defense. Let me know if you need me!"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Your contact information will automatically be shared with the captain.
                  </p>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setNotifySubModal({ isOpen: false, announcement: null })}
                    className="btn-secondary"
                    disabled={subNotificationStatus === 'success'}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={subNotificationStatus === 'success'}
                  >
                    Send Notification
                  </button>
                </div>
              </form>
            )}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
